import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams } from "react-router-dom";
import { Images } from "../../../../../../utils/imageConstants";
import { QUERY_KEY } from "../../../../../../services/queryEndPoint";
import { Api } from "../../../../../../services/interceptor";
import {
  type IGraphicsPayload,
  type IPlayersTeamSquad,
  type ITeamsSquad,
  type StreamerItem,
} from "./types/matchDetails.d";
import { graphicsSchema } from "./validation/matchValidation";
import { AppDialog } from "../../../../../../components/common/CustomMdal";
import BowlingScoreCardModal from "./modals/BowlingScoreCardModal";
import TeamModal from "./modals/TeamModal";
import PointsTable from "./modals/PointsTable";

interface PlayersProps {
  teamOnePlayers: ITeamsSquad[];
  teamTwoPlayers: ITeamsSquad[];
}

const STREAMER_ITEMS: StreamerItem[] = [
  { key: "Four", label: "Four", icon: Images.fours_streamer_icon },
  { key: "Six", label: "Six", icon: Images.six_streamer_icon },
  { key: "Wicket", label: "Wicket", icon: Images.wicket_streamer_icon },
  { key: "Innings Chart", label: "Innings Chart", icon: Images.inningChart_streamer_icon },
  { key: "Runs Chart", label: "Runs Chart", icon: Images.runsChart_streamer_icon },
  {
    key: "Partnership Chart",
    label: "Partnership Chart",
    icon: Images.partnership_streamer_icon,
  },
  { key: "Run Rate Chat", label: "Run Rate Chat", icon: Images.runRate_streamer_icon },
  { key: "Batter Chart", label: "Batter Chart", icon: Images.batter_streamer_icon },
  { key: "Bowler Chart", label: "Bowler Chart", icon: Images.bowler_streamer_icon },
  { key: "Team Extra Chart", label: "Team Extra Chart", icon: Images.teamExtra_streamer_icon },
  {
    key: "Match Extra Chart",
    label: "Match Extra Chart",
    icon: Images.matchExtra_streamer_icon,
  },
  { key: "Wagon Wheel", label: "Wagon Wheel", icon: Images.wagon_streamer_icon },
  {
    key: "Boundary Ball % Match",
    label: "Boundary Ball % Match",
    icon: Images.boundaryTeam_streamer_icon,
  },
  { key: "Squad Details", label: "Squad Details", icon: Images.squad_streamer_icon },
  { key: "Toss Win Ratio", label: "Toss Win Ratio", icon: Images.tossWin_streamer_icon },
  { key: "Batsman Details", label: "Batsman Details", icon: Images.batsman_streamer_icon },
  { key: "Bowler Details", label: "Bowler Details", icon: Images.bowlerDetails_streamer_icon },
  { key: "Shot Analysis", label: "Shot Analysis", icon: Images.shotAnalysis_streamer_icon },
  { key: "Player Stats", label: "Player Stats", icon: Images.playerStats_streamer_icon },
  { key: "Next Match", label: "Next Match", icon: Images.nextMatch_streamer_icon },
  { key: "Match Schedule", label: "Match Schedule", icon: Images.matchSchdule_streamer_icon },
  { key: "Win Predictor", label: "Win Predictor", icon: Images.winning_streamer_icon },
  { key: "Bowling Scorecard", label: "Bowling Scorecard", icon: Images.runsChart_streamer_icon },
  { key: "Points Table", label: "Points Table", icon: Images.streamer_icon },
];

const REQUIRE_CONFIRM = new Set<string>([
  "Player Stats",
  "Bowling Scorecard",
  "Points Table",
]);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MatchesStreamer = (_props: PlayersProps) => {
  const { matchId } = useParams();
  const teamId = "3467ea26-889c-4a0f-b9d2-e5f8bd0fbb79";
  const [bowlingScoreModal, setBowlingScoreModal] = useState(false);
  const [teamModal, setTeamModal] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [pointsTableModal, setPointsTableModal] = useState(false);

  const {
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<IGraphicsPayload>({
    resolver: yupResolver(graphicsSchema),
    defaultValues: {
      matchId: matchId ?? "",
      graphicsType: "",
      divisionId: "",
      inningId: "",
      playerId: "",
    },
  });

  const showGraphics = useMutation({
    mutationFn: (data: IGraphicsPayload) =>
      Api.put(QUERY_KEY.showGraphics, data),
    onSuccess: () => toast.success("Graphics posted"),
    onError: (error: unknown) => {
      toast.error("Failed to post graphics");
      console.error("send graphics error:", error);
    },
  });

  const buildPayload = (graphicsType: string): IGraphicsPayload | null => {
    const vals = getValues();
    const mId = matchId ?? vals.matchId ?? "";
    if (!mId) {
      toast.error("Match ID is required");
      return null;
    }
    return {
      matchId: mId,
      graphicsType,
      divisionId: vals.divisionId || "",
      inningId: vals.inningId || "",
      playerId: vals.playerId || "",
    };
  };

  const onSelectType = async (typeLabel: string) => {
    if (REQUIRE_CONFIRM.has(typeLabel)) {
      if (typeLabel === "Bowling Scorecard") {
        setBowlingScoreModal(true);
      }
      if (typeLabel === "Player Stats") {
        setTeamModal(true);
      }
      if (typeLabel === "Points Table") {
        setPointsTableModal(true);
      }
      return;
    }
    setSelectedType(typeLabel);
    setValue("graphicsType", typeLabel, { shouldValidate: false });
    const payload = buildPayload(typeLabel);
    if (!payload) {
      return;
    }
    await showGraphics.mutateAsync(payload);
  };

  const onCopy = async () => {
    const url = getStreamingUrl(matchId);
    try {
      await copyToClipboard(url);
      toast.success("Streaming URL copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const onClear = () => {
    setSelectedType("");
    reset({
      matchId: matchId ?? "",
      graphicsType: "",
      divisionId: "",
      inningId: "",
      playerId: "",
    });
    toast("Cleared");
  };

  function getStreamingUrl(matchId?: string): string {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    return `${base}/stream/${matchId ?? ""}`;
  }

  async function copyToClipboard(text: string) {
    if (navigator?.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(ta);
    }
  }

  const { data } = useQuery<IPlayersTeamSquad>({
    queryKey: [QUERY_KEY.getMatchInfo, matchId],
    queryFn: async () => {
      const res = await Api.get(
        `${QUERY_KEY.getMatchInfo}/${matchId}/${teamId}`
      );
      return res.data as IPlayersTeamSquad;
    },
  });
  const players: ITeamsSquad[] = data?.playingSquad ?? [];

  return (
    <>
      <section className="streamer-main-wrapper" aria-label="Streamer options">
        <input type="hidden" name="matchId" value={matchId ?? ""} />
        <input type="hidden" name="graphicsType" value={selectedType} />

        <ul
          className="streamer-wrapper"
          role="list"
          aria-label="Streamer items"
        >
          {STREAMER_ITEMS.map((item) => {
            const active = selectedType === item.label;
            return (
              <li
                key={item.key}
                className={`streamer-wrap ${active ? "is-active" : ""}`}
                role="listitem"
                tabIndex={0}
                onClick={() => onSelectType(item.label)}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") &&
                  onSelectType(item.label)
                }
                aria-pressed={active}
                aria-label={`Select ${item.label}`}
              >
                <img
                  className="streamer-icon"
                  alt={`${item.label} icon`}
                  src={item.icon}
                  aria-hidden="true"
                />
                <p className="streamer-text">{item.label}</p>
              </li>
            );
          })}
        </ul>

        <div className="button-wrap" role="group" aria-label="Streamer actions">
          <button
            type="button"
            className="cancel-button-wrap"
            aria-label="Copy streaming URL"
            onClick={onCopy}
            disabled={showGraphics.isPending}
          >
            <img
              className="cancel-icon"
              alt="copy-icon"
              src={Images.copy_icon}
              aria-hidden="true"
            />
            <span className="copy-wrapper">
              <span className="copy-text">
                {showGraphics.isPending ? "..." : "Copy"}
              </span>
            </span>
          </button>

          <button
            type="button"
            className="clear-ticker-wrap"
            aria-label="Clear ticker"
            onClick={onClear}
            disabled={showGraphics.isPending}
          >
            <img
              className="clear-ticker-icon"
              alt="clear-ticker"
              aria-hidden="true"
              src={Images.clear_ticker_icon}
            />
            <span className="copy-wrapper">
              <span className="copy-text">Clear Ticker</span>
            </span>
          </button>
        </div>
        {(errors.matchId || errors.graphicsType) && (
          <div role="alert" className="form-errors" style={{ marginTop: 8 }}>
            {errors.matchId?.message && <p>{errors.matchId.message}</p>}
            {errors.graphicsType?.message && (
              <p>{errors.graphicsType.message}</p>
            )}
          </div>
        )}
      </section>

      <AppDialog
        open={bowlingScoreModal}
        onOpenChange={setBowlingScoreModal}
        title={null}
        description={null}
        size="sm"
      >
        <BowlingScoreCardModal
          teamOneName="India"
          teamTwoName="Pakistan"
          onSelectInning={(n) => {
            console.log("Selected inning:", n);
            setBowlingScoreModal(false);
          }}
        />
      </AppDialog>

      <AppDialog
        open={teamModal}
        onOpenChange={setTeamModal}
        title={null}
        description={null}
        size="lg"
      >
        <TeamModal players={players} />
      </AppDialog>

      <AppDialog
        open={pointsTableModal}
        onOpenChange={setPointsTableModal}
        title={null}
        description={null}
        size="lg"
      >
        <PointsTable />
      </AppDialog>
    </>
  );
};

export default MatchesStreamer;
